/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import MetaContext from './MetaContext';
import initialMetaState from './initialMetaState';
import metaReducer from 'contexts/meta/reducers/reducer/meta.reducer';
import * as action from 'contexts/meta/reducers/actions/meta.actions';
import * as mapAction from 'contexts/meta/reducers/actions/meta.map.actions';
import * as mapFieldAction from 'contexts/meta/reducers/actions/meta.mapFields.actions';
import * as sftpAction from 'contexts/meta/reducers/actions/meta.sftp.actions';
import * as shopifyAction from 'contexts/meta/reducers/actions/meta.shopify.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { ChildrenProps } from 'interfaces/general.interface';
import * as metaResponseInterface from 'api/endpoints/meta/meta.response.interface';
import * as metaInterface from 'interfaces/meta.interface';
import { TupdateShopifyConnectionAPIBodyUpdateProps } from 'api/endpoints/superAdmin/superAdmin.response.interface';
import useContextSuperAdmin from 'hooks/contexts/useContextSuperAdmin';
import {
  IcreateMapFieldProps,
  IShopifyQueryItem,
  IShopifyTemplateItem,
  IupdateMapFieldBodyProps,
  IUpdateQueryProps,
} from 'interfaces/superAdmin.interface';
import { NavigateFunction } from 'react-router-dom';
import useContextCompany from 'hooks/contexts/useContextCompany';

function MetaProvider(props: ChildrenProps) {
  const [metaState, dispatch] = useReducer(metaReducer, initialMetaState);
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();
  const { company } = useContextCompany();
  const { shopify } = useContextSuperAdmin();

  const getConnections = useCallback(async () => {
    if (!company?.id) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    const body: metaResponseInterface.TgetConnectorsAPIBodyProps = {
      client_id: company.id,
      search: [],
      index: 0,
      limit: 1000,
    };
    return await action.getConnectionsAction({ dispatch, body, token, translate });
  }, [company?.id]);

  const setConnectionSelected = useCallback((connectionId: string | null) => {
    return action.setConnectionSelectedAction({ dispatch, connectionId });
  }, []);

  // MAP //////////////////////////////////////////////////
  const getMaps = useCallback(async ({ clientId }: metaInterface.IgetMapsProps) => {
    const token = await getAccessTokenSilently();
    return await mapAction.getMapsAction({
      dispatch,
      clientId,
      token,
      translate,
    });
  }, []);

  const setMapSelected = useCallback((mapId: string | null) => {
    return mapAction.setMapSelectedAction({ dispatch, mapId });
  }, []);

  const createMap = useCallback(
    async ({ connectorType, body, connection }: metaInterface.ICreateMapProps) => {
      const token = await getAccessTokenSilently();
      return await mapAction.createMapAction({
        dispatch,
        connectorType,
        body,
        connection,
        token,
        translate,
      });
    },
    []
  );

  const deleteMap = useCallback(async ({ mapId }: { mapId: string }) => {
    const token = await getAccessTokenSilently();
    return await mapAction.deleteMapAction({
      dispatch,
      mapId,
      token,
      translate,
    });
  }, []);

  const updateMap = useCallback(
    async ({ connectorType, body }: metaInterface.IUpdateMapProps) => {
      const token = await getAccessTokenSilently();
      return await mapAction.updateMapAction({
        dispatch,
        connectorType,
        body,
        token,
        translate,
      });
    },
    []
  );

  const addMapToConnection = useCallback(
    async ({
      connectionId,
      mapIds,
      connectorType,
    }: metaInterface.IaddMapToConnectionProps) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await mapAction.addMapToConnectorAction({
        dispatch,
        companyId: company.id,
        connectionId,
        mapIds,
        connectorType,
        token,
        translate,
      });
    },
    [company?.id]
  );

  const deleteMapFromConnection = useCallback(
    async ({
      connectionId,
      mapIds,
      connectorType,
    }: metaInterface.IdeleteMapFromConnectionProps) => {
      const token = await getAccessTokenSilently();
      return await mapAction.deleteMapFromConnectionAction({
        dispatch,
        connectionId,
        mapIds,
        connectorType,
        token,
        translate,
      });
    },
    []
  );

  const addMapFieldsToMap = useCallback(
    async ({ mapId, mapFields }: metaInterface.IaddMapFieldsToMapProps) => {
      const token = await getAccessTokenSilently();
      return await mapAction.addMapFieldsToMapAction({
        dispatch,
        mapId,
        mapFields,
        mappings: metaState.mappings,
        token,
        translate,
      });
    },
    [metaState.mappings]
  );
  /////////////////////////////////////////////////////////

  // Map fields //////////////////////////////////////////////////////////////
  const getMapFields = useCallback(async () => {
    if (!company?.id) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await mapFieldAction.getMapFieldsAction({
      dispatch,
      token,
      translate,
      clientId: company.id,
    });
  }, [company?.id]);

  const createMapField = useCallback(
    async ({ fieldData, mapId }: IcreateMapFieldProps) => {
      const token = await getAccessTokenSilently();
      return await mapFieldAction.createMapFieldAction({
        dispatch,
        fieldData,
        mapId,
        mappings: metaState.mappings,
        token,
        translate,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metaState.mappings]
  );

  const updateMapField = useCallback(
    async ({ filter, update }: IupdateMapFieldBodyProps) => {
      const token = await getAccessTokenSilently();
      return await mapFieldAction.updateMapFieldAction({
        dispatch,
        body: { filter, update },
        token,
        translate,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const deleteMapField = useCallback(
    async ({ mapFieldId }: { mapFieldId: string }) => {
      const token = await getAccessTokenSilently();
      return await mapFieldAction.deleteMapFieldAction({
        dispatch,
        mapFieldId,
        token,
        translate,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const deleteMapFields = useCallback(
    async ({ mapFieldsIds }: { mapFieldsIds: string[] }) => {
      const token = await getAccessTokenSilently();
      return await mapFieldAction.deleteMapFieldsAction({
        dispatch,
        mapFieldsIds,
        token,
        translate,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const removeMapFieldFromMap = useCallback(
    async ({
      mapId,
      mapFieldId,
      deleteMapField,
    }: {
      mapId: string;
      mapFieldId: string;
      deleteMapField: boolean;
    }) => {
      const token = await getAccessTokenSilently();
      return await mapFieldAction.removeMapFieldFromMapAction({
        dispatch,
        mapId,
        mapFieldId,
        deleteMapField,
        mappings: metaState.mappings,
        token,
        translate,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metaState.mappings]
  );

  const removeMapFieldsFromMap = useCallback(
    async ({ mapId, mapFieldsIds }: { mapId: string; mapFieldsIds: string[] }) => {
      const token = await getAccessTokenSilently();
      return await mapFieldAction.removeMapFieldsFromMapAction({
        dispatch,
        mapId,
        mapFieldsIds,
        mappings: metaState.mappings,
        token,
        translate,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metaState.mappings]
  );

  // Map fields templates
  const getMapFieldsTemplates = useCallback(async () => {
    if (!company?.id) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await mapFieldAction.getMapFieldsTemplatesAction({
      dispatch,
      token,
      translate,
      clientId: company.id,
    });
  }, [getAccessTokenSilently, company?.id]);

  const deleteMapFieldTemplate = useCallback(
    async ({ templateName }: { templateName: string }) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await mapFieldAction.deleteMapFieldTemplateAction({
        dispatch,
        templateName,
        token,
        translate,
        clientId: company.id,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [company?.id]
  );

  const copyMapFieldsTemplate = useCallback(
    async ({ templateTag }: { templateTag: string }) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await mapFieldAction.copyMapFieldsTemplateAction({
        dispatch,
        templateTag,
        token,
        translate,
        clientId: company.id,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [company?.id]
  );
  ////////////////////////////////////////////////////////////////////////////

  // SFTP /////////////////////////////////////////////////
  const createSftpConnection = useCallback(
    async (body: metaResponseInterface.TcreateSftpUserAPIBodyProps) => {
      const token = await getAccessTokenSilently();
      return (await sftpAction.createSftpConnectionAction(
        dispatch,
        body,
        token,
        translate
      )) as boolean;
    },
    []
  );

  const updateSftpConnection = useCallback(async (body: metaInterface.TSftpUserItem) => {
    const token = await getAccessTokenSilently();
    return await sftpAction.updateSftpConnectionAction(dispatch, body, token, translate);
  }, []);

  const removeSftpConnection = useCallback(async (user_name: string) => {
    const token = await getAccessTokenSilently();
    sftpAction.deleteSftpConnectionAction(dispatch, user_name, token, translate);
  }, []);
  /////////////////////////////////////////////////////////

  // Shopify //////////////////////////////////////////////
  const createShopifyConnection = useCallback(
    async ({
      body,
      navigate,
    }: {
      body: metaResponseInterface.TcreateShopifyConnectionAPIBodyProps;
      navigate: NavigateFunction;
    }) => {
      const token = await getAccessTokenSilently();
      return (await shopifyAction.createShopifyConnectionAction({
        dispatch,
        body,
        queries: metaState.shopify.queries,
        navigate,
        token,
        translate,
      })) as boolean;
    },
    [shopify.queries]
  );

  const updateShopifyConnection = useCallback(
    async ({
      body,
      connectionId,
    }: {
      body: TupdateShopifyConnectionAPIBodyUpdateProps;
      connectionId: string;
    }) => {
      const token = await getAccessTokenSilently();
      return (await shopifyAction.updateShopifyConnectionAction({
        dispatch,
        body,
        connectionId,
        token,
        translate,
      })) as boolean;
    },
    []
  );

  const removeShopifyConnection = useCallback(
    async ({ connectionId }: { connectionId: string }) => {
      const token = await getAccessTokenSilently();
      return (await shopifyAction.deleteShopifyConnectionAction({
        dispatch,
        connectionId,
        token,
        translate,
      })) as boolean;
    },
    []
  );

  // Configuration Shopify
  const getShopifyConfiguration = useCallback(
    async ({ connectorId }: metaInterface.IgetConfigurationProps) => {
      const token = await getAccessTokenSilently();
      return await shopifyAction.getShopifyConfigurationAction({
        dispatch,
        connectorId,
        token,
        translate,
      });
    },
    []
  );

  const copyShopifyQueriesTemplate = useCallback(
    async ({ template }: metaInterface.IcreateUpdateConfigurationProps) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await shopifyAction.copyShopifyQueriesTemplateAction({
        dispatch,
        clientId: company.id,
        template,
        token,
        translate,
      });
    },
    [company?.id]
  );

  const addShopifyQueriesToConfiguration = useCallback(
    async ({ connectorId, queries }: metaInterface.IaddShopifyQueriesToConfiguration) => {
      const token = await getAccessTokenSilently();
      return await shopifyAction.addShopifyQueriesToConfigurationAction({
        dispatch,
        connectorId,
        queries,
        token,
        translate,
      });
    },
    []
  );

  // Queries Shopify /////////////////////////////////////////////////////////
  const getShopifyQueries = useCallback(async () => {
    if (!company?.id) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await shopifyAction.getShopifyQueriesAction(
      dispatch,
      company.id,
      token,
      translate
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const selectShopifyQuery = useCallback((query: IShopifyQueryItem) => {
    return shopifyAction.selectShopifyQueryAction(dispatch, query);
  }, []);

  const deleteShopifyQuery = useCallback(
    async ({ queryId }: { queryId: string }) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await shopifyAction.deleteShopifyQueryAction({
        dispatch,
        clientId: company.id,
        queryId,
        token,
        translate,
      });
    },
    [company?.id]
  );

  const updateShopifyQuery = useCallback(
    async ({
      queryId,
      query,
      tag,
      description,
      indexable,
      scope,
      audience,
      showSuccessMsg,
    }: IUpdateQueryProps & { showSuccessMsg?: boolean }) => {
      const token = await getAccessTokenSilently();
      return await shopifyAction.updateShopifyQueryAction({
        dispatch,
        dataToUpdate: { queryId, query, tag, description, indexable, scope, audience },
        showSuccessMsg,
        token,
        translate,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Templates Shopify
  const getShopifyTemplates = useCallback(async () => {
    if (!company?.id) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await shopifyAction.getShopifyTemplatesAction({
      dispatch,
      clientId: company.id,
      token,
      translate,
    });
  }, [company?.id]);

  const selectShopifyTemplate = useCallback(
    ({ template }: { template: IShopifyTemplateItem }) => {
      return shopifyAction.selectShopifyTemplateAction({ dispatch, template });
    },
    []
  );

  const deleteShopifyTemplate = useCallback(
    async ({ templateTag }: { templateTag: string }) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await shopifyAction.deleteShopifyTemplateAction({
        dispatch,
        clientId: company.id,
        templateTag,
        token,
        translate,
      });
    },
    [company?.id]
  );
  /////////////////////////////////////////////////////////

  const memoProvider = useMemo(
    () => ({
      ...metaState,
      getConnections,
      // Maps
      getMaps,
      setMapSelected,
      createMap,
      deleteMap,
      updateMap,
      addMapToConnection,
      deleteMapFromConnection,
      addMapFieldsToMap,
      // Map fields
      getMapFields,
      createMapField,
      updateMapField,
      deleteMapField,
      deleteMapFields,
      removeMapFieldFromMap,
      removeMapFieldsFromMap,
      // Map fields templates
      getMapFieldsTemplates,
      deleteMapFieldTemplate,
      copyMapFieldsTemplate,
      sftp: {
        ...metaState.sftp,
        createSftpConnection,
        updateSftpConnection,
        setConnectionSelected,
        removeSftpConnection,
      },
      shopify: {
        ...metaState.shopify,
        createShopifyConnection,
        updateShopifyConnection,
        removeShopifyConnection,
        // Configuration
        getShopifyConfiguration,
        copyShopifyQueriesTemplate,
        addShopifyQueriesToConfiguration,
        // Queries
        getShopifyQueries,
        selectShopifyQuery,
        // createShopifyQuery,
        deleteShopifyQuery,
        updateShopifyQuery,
        // Templates
        getShopifyTemplates,
        selectShopifyTemplate,
        // createUpdateShopifyTemplate,
        deleteShopifyTemplate,
      },
    }),
    [
      metaState,
      getConnections,
      // Maps
      getMaps,
      setMapSelected,
      createMap,
      deleteMap,
      updateMap,
      addMapToConnection,
      deleteMapFromConnection,
      addMapFieldsToMap,
      // Map fields
      getMapFields,
      createMapField,
      updateMapField,
      deleteMapField,
      deleteMapFields,
      removeMapFieldFromMap,
      removeMapFieldsFromMap,
      // Map fields templates
      getMapFieldsTemplates,
      deleteMapFieldTemplate,
      copyMapFieldsTemplate,
      // SFTP
      createSftpConnection,
      updateSftpConnection,
      setConnectionSelected,
      removeSftpConnection,
      // Shopify
      createShopifyConnection,
      updateShopifyConnection,
      removeShopifyConnection,
      // Configuration
      getShopifyConfiguration,
      copyShopifyQueriesTemplate,
      addShopifyQueriesToConfiguration,
      // Queries
      getShopifyQueries,
      selectShopifyQuery,
      // createShopifyQuery,
      deleteShopifyQuery,
      updateShopifyQuery,
      // Templates
      getShopifyTemplates,
      selectShopifyTemplate,
      // createUpdateShopifyTemplate,
      deleteShopifyTemplate,
    ]
  );

  return (
    <MetaContext.Provider value={memoProvider}>{props.children}</MetaContext.Provider>
  );
}

export default memo(MetaProvider);
